/**
 * BranchSelector - Branch selection dropdown for multi-branch users
 *
 * Only visible when user has access to multiple branches.
 * Single-branch users don't see this component.
 */

import { useState, useRef, useEffect } from 'react';
import { useBranchStore, useShouldShowBranchSelector, type Branch } from '../../stores/branchStore';

interface BranchSelectorProps {
  /** Variant affects styling */
  variant?: 'header' | 'inline';
  /** Additional CSS classes */
  className?: string;
}

export function BranchSelector({ variant = 'header', className = '' }: BranchSelectorProps) {
  const {
    userBranches,
    currentBranchId,
    setCurrentBranch,
  } = useBranchStore();

  const shouldShow = useShouldShowBranchSelector();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render for single-branch users
  if (!shouldShow || userBranches.length <= 1) {
    return null;
  }

  const currentBranch = currentBranchId
    ? userBranches.find(b => b.id === currentBranchId)
    : null;

  const handleSelect = (branchId: string | null) => {
    setCurrentBranch(branchId);
    setIsOpen(false);
  };

  if (variant === 'header') {
    return (
      <div ref={dropdownRef} className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm text-white"
        >
          {currentBranch ? (
            <>
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: currentBranch.color || '#6B7280' }}
              />
              <span>{currentBranch.code}</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-forest-500 to-sage-500" />
              <span>All Branches</span>
            </>
          )}
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-sage-200 py-1 z-50">
            {/* All Branches option */}
            <button
              onClick={() => handleSelect(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-sage-50 transition-colors ${
                !currentBranchId ? 'bg-primary-50 text-primary-700' : 'text-sage-700'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-forest-500 to-sage-500" />
              <span>All Branches</span>
              {!currentBranchId && (
                <svg className="w-4 h-4 ml-auto text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            <div className="h-px bg-sage-200 my-1" />

            {/* Individual branches */}
            {userBranches.map((branch) => (
              <button
                key={branch.id}
                onClick={() => handleSelect(branch.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-sage-50 transition-colors ${
                  currentBranchId === branch.id ? 'bg-primary-50 text-primary-700' : 'text-sage-700'
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: branch.color || '#6B7280' }}
                />
                <span>{branch.name}</span>
                <span className="text-sage-400 text-xs ml-auto mr-1">{branch.code}</span>
                {currentBranchId === branch.id && (
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Inline variant - simple dropdown select
  return (
    <select
      value={currentBranchId || 'all'}
      onChange={(e) => handleSelect(e.target.value === 'all' ? null : e.target.value)}
      className={`px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none bg-white text-sm ${className}`}
    >
      <option value="all">All Branches</option>
      {userBranches.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name} ({branch.code})
        </option>
      ))}
    </select>
  );
}

/**
 * BranchBadge - Small badge showing current branch
 * Used for compact display in headers/cards
 */
export function BranchBadge({ branch }: { branch: Branch | null }) {
  if (!branch) return null;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: `${branch.color}20` || '#6B728020',
        color: branch.color || '#6B7280',
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: branch.color || '#6B7280' }}
      />
      {branch.code}
    </span>
  );
}

/**
 * BranchField - Form field for selecting branch on new records
 * Hidden for single-branch users (auto-assigns their branch)
 */
interface BranchFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  className?: string;
}

export function BranchField({
  value,
  onChange,
  label = 'Branch',
  required = false,
  className = '',
}: BranchFieldProps) {
  const { userBranches, isMultiBranchUser } = useBranchStore();
  const shouldShow = useShouldShowBranchSelector();

  // Single-branch users don't see this field
  if (!shouldShow || !isMultiBranchUser) {
    return null;
  }

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-sage-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none bg-white"
      >
        {userBranches.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {branch.name} ({branch.code})
          </option>
        ))}
      </select>
    </div>
  );
}
